/**
 * PoliceEventModal – Reaktives Popup bei Polizei-Kontrolle.
 * Erscheint wenn Backend ein police_event sendet (via WS oder job-complete HTTP-Response).
 * Timer: 30 Sekunden clientseitig. Bei Ablauf: TIMEOUT an den Server — welche Folge das hat,
 * entscheidet ausschließlich das Backend (Behörden-Matrix, NT-FAHNDUNG-02).
 * Die sichtbaren Schaltflächen kommen aus eventData.options (Backend), nicht aus dem Client.
 * Schnittstelle: showPoliceEventModal(eventData) aus dashboard.ts aufrufen.
 */

import { api } from '../services/api.js';
import { gameState } from '../state/gameState.js';
import { updateWantedIndicator } from './WantedIndicator.js';
import { formatWantedLevel, getWantedLevelLabel } from '../utils/wanted-labels.js';
import { refreshBalance } from '../utils/refreshBalance.js';
import { calcNoticeDisplayMs } from '../utils/notice-duration.js';

const MODAL_ID = 'police-event-modal';
const TIMER_DURATION_S = 30;

type PoliceEventAction = 'PAY_FINE' | 'ARGUE' | 'BRIBE' | 'RUN' | 'SILENCE' | 'SURRENDER';
type PoliceEventResponseAction = PoliceEventAction | 'TIMEOUT';

export interface PoliceEventData {
  eventId: string;
  isBribable: boolean;
  bribeCostCents: string;
  runSuccessChance: number;
  wantedLevel: number;
  options: PoliceEventAction[];
  timeoutAction: PoliceEventAction;
  fineCostCents: string;
  argueSuccessChance: number;
  silenceSuccessChance: number;
}

interface RespondResult {
  outcome:
    | 'ESCAPED' | 'ARRESTED' | 'BRIBED' | 'SURRENDERED'
    | 'FINE_PAID' | 'ARGUE_WON' | 'ARGUE_LOST' | 'SILENCE_WON';
  wantedPointsDelta: number;
  newWantedLevel: number;
  newWantedPoints: number;
  costCents?: string;
  reputationDelta?: number;
  shiftPenaltySeconds?: number;
  message: string;
}

let countdownTimer: ReturnType<typeof setInterval> | null = null;

function formatNox(cents: string): string {
  return (Number(cents) / 100).toFixed(2);
}

/** FL0 ist ein Zustand (SAUBER) — kontrolliert wird trotzdem vom Ordnungsamt. */
function actingAuthority(level: number): string {
  return level <= 0 ? getWantedLevelLabel(1) : getWantedLevelLabel(level);
}

function renderOptionButton(action: PoliceEventAction, eventData: PoliceEventData): string {
  const pct = (chance: number): string => String(Math.round(chance * 100));
  switch (action) {
    case 'PAY_FINE': {
      const balance = BigInt(gameState.getPlayer()?.noxBalance ?? '0');
      const affordable = balance >= BigInt(eventData.fineCostCents);
      return `<button class="police-btn police-btn--bribe${affordable ? '' : ' police-btn--disabled'}" data-police-action="PAY_FINE" type="button"${affordable ? '' : ' disabled'}>
        BUSSGELD ZAHLEN &ndash; ${formatNox(eventData.fineCostCents)}&nbsp;&#8358;</button>`;
    }
    case 'ARGUE':
      return `<button class="police-btn police-btn--run" data-police-action="ARGUE" type="button">
        DISKUTIEREN&nbsp;(${pct(eventData.argueSuccessChance)}%)</button>`;
    case 'BRIBE':
      return `<button class="police-btn police-btn--bribe${eventData.isBribable ? '' : ' police-btn--disabled'}" data-police-action="BRIBE" type="button"${eventData.isBribable ? '' : ' disabled'}>
        SCHMIEREN &ndash; ${formatNox(eventData.bribeCostCents)}&nbsp;&#8358;</button>`;
    case 'RUN':
      return `<button class="police-btn police-btn--run" data-police-action="RUN" type="button">
        WEGRENNEN&nbsp;(${String(eventData.runSuccessChance)}%)</button>`;
    case 'SILENCE':
      return `<button class="police-btn police-btn--run" data-police-action="SILENCE" type="button">
        SCHWEIGEN&nbsp;(${pct(eventData.silenceSuccessChance)}%)</button>`;
    case 'SURRENDER':
      return `<button class="police-btn police-btn--surrender" data-police-action="SURRENDER" type="button">
        VERHAFTEN LASSEN</button>`;
  }
}

function timeoutLabel(action: PoliceEventAction): string {
  return action === 'PAY_FINE' ? 'dann wird das Bußgeld fällig' : 'dann wirst du verhaftet';
}

export function showPoliceEventModal(eventData: PoliceEventData): void {
  removeModal();

  const overlay = document.createElement('div');
  overlay.id = MODAL_ID;
  overlay.className = 'modal-backdrop modal-backdrop--elevated';

  const flavour = getFlavourText(eventData.wantedLevel);
  const showBribeWarning = eventData.options.includes('BRIBE') && !eventData.isBribable;

  overlay.innerHTML = `
    <div class="modal police-modal" role="dialog" aria-modal="true" aria-label="Kontrolle">
      <div class="modal-header police-modal-header">
        <span class="police-modal-fl">[ ${formatWantedLevel(eventData.wantedLevel)} ]</span>
        <h2 class="police-modal-title">KONTROLLE: ${actingAuthority(eventData.wantedLevel)}</h2>
      </div>
      <div class="modal-body">
        <p class="police-modal-flavour">${flavour}</p>
        ${showBribeWarning ? '<p class="police-modal-warn">Der lässt sich nicht kaufen.</p>' : ''}
        <div class="police-modal-timer">
          <span id="police-timer-value">${String(TIMER_DURATION_S)}</span>s
          <span class="police-timer-label"> – ${timeoutLabel(eventData.timeoutAction)}</span>
        </div>
      </div>
      <div class="modal-actions police-modal-actions">
        ${eventData.options.map((action) => renderOptionButton(action, eventData)).join('')}
      </div>
    </div>
  `;

  document.body.appendChild(overlay);
  injectStyles();

  let remaining = TIMER_DURATION_S;
  const timerEl = document.getElementById('police-timer-value');

  countdownTimer = setInterval(() => {
    remaining--;
    if (timerEl) {
      timerEl.textContent = String(remaining);
      if (remaining <= 10) timerEl.style.color = 'var(--accent-red)';
    }
    if (remaining <= 0) {
      void respond(eventData.eventId, 'TIMEOUT');
    }
  }, 1000);

  overlay.querySelectorAll<HTMLButtonElement>('[data-police-action]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const action = btn.dataset.policeAction as PoliceEventAction | undefined;
      if (action) void respond(eventData.eventId, action);
    });
  });
}

async function respond(
  eventId: string,
  action: PoliceEventResponseAction,
): Promise<void> {
  stopTimer();

  const modal = document.getElementById(MODAL_ID);
  if (modal) {
    const btns = modal.querySelectorAll<HTMLButtonElement>('button');
    btns.forEach((b) => { b.disabled = true; });
  }

  try {
    const result = await api.post<{ data: RespondResult }>(
      '/police/event/respond',
      { eventId, action },
    );

    // Bewusst per Doppel-Cast statt direktem result.data gelesen: der api-Client entpackt eine
    // {data,meta}-Hülle nur, wenn der Backend-Response tatsächlich so geformt ist (siehe
    // hasDataEnvelope() in services/api.ts) - liefert der Endpunkt das Payload flach, ist
    // result zur Laufzeit bereits RespondResult selbst, obwohl der generische Typ hier
    // {data:RespondResult} behauptet. Beide Formen werden hier abgefangen.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion, @typescript-eslint/no-unnecessary-condition -- s.o.
    const data = (result as unknown as { data: RespondResult }).data ?? (result as unknown as RespondResult);

    updateGameStateFromResult(data);
    showOutcome(data);
  } catch {
    removeModal();
  }
}

function updateGameStateFromResult(result: RespondResult): void {
  const player = gameState.getPlayer();
  if (!player) return;

  if (result.outcome === 'ARRESTED' || result.outcome === 'SURRENDERED') {
    gameState.setPlayer({
      ...player,
      wantedLevel: 0,
      wantedPoints: 0,
      isInJail: true,
    });
  } else {
    gameState.setPlayer({
      ...player,
      wantedLevel: result.newWantedLevel,
      wantedPoints: result.newWantedPoints,
    });
  }
  updateWantedIndicator();

  // Bußgeld/Schmieren/Ruf-Abzug: Kontostand + Ruf frisch vom Server holen.
  if (result.costCents !== undefined || (result.reputationDelta ?? 0) !== 0) {
    void refreshBalance();
  }
}

function showOutcome(result: RespondResult): void {
  const modal = document.getElementById(MODAL_ID);
  if (!modal) return;

  const isArrest = result.outcome === 'ARRESTED' || result.outcome === 'SURRENDERED';

  const modalBody = modal.querySelector('.police-modal');
  if (!modalBody) return;
  modalBody.innerHTML = `
    <div class="modal-header police-modal-header">
      <h2 class="police-modal-title">${getOutcomeTitle(result.outcome)}</h2>
    </div>
    <div class="modal-body">
      <p class="police-modal-flavour">${result.message}</p>
      ${isArrest ? '<p class="police-modal-warn">Du wirst zum Knast gebracht.</p>' : ''}
    </div>
    <div class="modal-actions police-modal-actions">
      <button class="police-btn police-btn--surrender" id="police-btn-close" type="button">
        SCHLIESSEN
      </button>
    </div>
  `;

  document.getElementById('police-btn-close')?.addEventListener('click', () => {
    removeModal();
    if (isArrest) {
      import('../router.js').then(({ navigate }) => { navigate('/jail'); }).catch(() => undefined);
    }
  });

  // Ergebnis einer Kontrolle ist wichtig — immer mit der längeren Fehler-Untergrenze
  setTimeout(() => {
    removeModal();
    if (isArrest) {
      import('../router.js').then(({ navigate }) => { navigate('/jail'); }).catch(() => undefined);
    }
  }, calcNoticeDisplayMs(result.message, true));
}

function getOutcomeTitle(outcome: string): string {
  switch (outcome) {
    case 'BRIBED': return 'BESTECHUNG ERFOLGREICH';
    case 'ESCAPED': return 'ENTKOMMEN';
    case 'ARRESTED': return 'VERHAFTET';
    case 'SURRENDERED': return 'VERHAFTET';
    case 'FINE_PAID': return 'BUSSGELD BEZAHLT';
    case 'ARGUE_WON': return 'VERWARNUNG';
    case 'ARGUE_LOST': return 'DISKUSSION VERLOREN';
    case 'SILENCE_WON': return 'VERFAHREN EINGESTELLT';
    default: return 'ERGEBNIS';
  }
}

function getFlavourText(level: number): string {
  if (level >= 5) return 'Das SEK stürmt rein. Mehr Helme als Fenster. Hier wird nicht mehr verhandelt.';
  if (level >= 4) return 'Zwei Herren vom LKA in identischen Jacken. Sie haben eine Akte. Sie ist dick.';
  if (level >= 3) return 'Die Kripo bittet zum Gespräch. Der Kaffee ist kalt, die Fragen sind es nicht.';
  if (level >= 2) return 'Routinekontrolle der Streife. Der Beamte prüft deinen Ausweis und deine Nerven.';
  return 'Das Ordnungsamt hält dich an. Verdacht auf Ordnungswidrigkeit, Formular liegt bereits ausgefüllt vor.';
}

function stopTimer(): void {
  if (countdownTimer !== null) {
    clearInterval(countdownTimer);
    countdownTimer = null;
  }
}

function removeModal(): void {
  stopTimer();
  document.getElementById(MODAL_ID)?.remove();
}

function injectStyles(): void {
  const styleId = 'police-modal-styles';
  if (document.getElementById(styleId)) return;

  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    /* Shell = .modal-backdrop/.modal aus dashboard.css (siehe components/BottomSheet.ts).
       .police-modal ist nur die Rot-Akzent-Themenvariante fuer dieses Modal. */
    .police-modal { border-color: var(--accent-red); }
    .police-modal-header {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      gap: 12px;
    }
    .police-modal-fl {
      font-size: 0.65rem;
      color: var(--accent-red);
      border: 1px solid var(--accent-red);
      padding: 1px 5px;
    }
    .police-modal .police-modal-title {
      font-size: 0.85rem;
      color: var(--text-primary);
      letter-spacing: 0.15em;
      text-transform: uppercase;
    }
    .police-modal-flavour {
      color: var(--text-muted);
      font-size: 0.75rem;
      margin-bottom: 10px;
      line-height: 1.6;
    }
    .police-modal-warn {
      color: var(--accent-orange);
      font-size: 0.7rem;
      margin-bottom: 10px;
    }
    .police-modal-actions {
      flex-direction: column;
      gap: 6px;
    }
    .police-btn {
      font-family: var(--font-mono);
      font-size: 0.72rem;
      letter-spacing: 0.08em;
      padding: 8px 12px;
      border: 1px solid var(--border);
      background: var(--bg-surface);
      color: var(--text-primary);
      text-transform: uppercase;
      cursor: pointer;
      text-align: left;
      transition: border-color 0.15s, background 0.15s;
    }
    .police-btn:hover:not(:disabled) { border-color: var(--text-primary); background: var(--bg-elevated); }
    .police-btn--bribe { border-color: var(--accent-gold); color: var(--accent-gold); }
    .police-btn--bribe:hover:not(:disabled) { background: rgba(240,180,41,0.08); }
    .police-btn--run { border-color: var(--accent-green); color: var(--accent-green); }
    .police-btn--run:hover:not(:disabled) { background: rgba(82,183,136,0.08); }
    .police-btn--surrender { border-color: var(--accent-red); color: var(--accent-red); }
    .police-btn--surrender:hover:not(:disabled) { background: rgba(230,57,70,0.08); }
    .police-btn--disabled, .police-btn:disabled {
      opacity: 0.35;
      cursor: not-allowed;
      border-color: var(--border);
      color: var(--text-muted);
    }
    .police-modal-timer {
      font-size: 0.65rem;
      color: var(--text-muted);
      text-align: right;
    }
    #police-timer-value {
      font-size: 0.8rem;
      color: var(--accent-gold);
      font-weight: bold;
    }
  `;
  document.head.appendChild(style);
}
